import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings({ user, setUser, onLogout }) {
  const location = useLocation();
  const [settings, setSettings] = useState({
    quality: "high",
    format: "original"
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.settings) {
      setSettings(user.settings);
    }
  }, [user]);

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/settings`, { settings });
      setUser(response.data);
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-container" data-testid="settings-page">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <Download size={28} color="#0ea5e9" />
          <h1>Pixiv Downloader</h1>
        </div>
        <nav className="dashboard-nav">
          <Link
            to="/"
            className={`nav-link ${location.pathname === "/" ? "active" : ""}`}
            data-testid="nav-dashboard"
          >
            Dashboard
          </Link>
          <Link
            to="/history"
            className={`nav-link ${location.pathname === "/history" ? "active" : ""}`}
            data-testid="nav-history"
          >
            History
          </Link>
          <Link
            to="/settings"
            className={`nav-link ${location.pathname === "/settings" ? "active" : ""}`}
            data-testid="nav-settings"
          >
            Settings
          </Link>
          <ThemeToggle />
          <button
            onClick={onLogout}
            className="logout-button"
            data-testid="logout-button"
          >
            <LogOut size={16} style={{ display: 'inline', marginRight: '6px' }} />
            Logout
          </button>
        </nav>
      </header>

      <main className="dashboard-content">
        <section className="welcome-section">
          <h2>Settings</h2>
          <p>Configure your download preferences</p>
        </section>

        <div className="settings-card">
          <h3>Download Preferences</h3>
          
          <div className="settings-group">
            <label htmlFor="quality">Image Quality</label>
            <select
              data-testid="quality-select"
              id="quality"
              value={settings.quality}
              onChange={(e) => handleChange("quality", e.target.value)}
            >
              <option value="low">Low (Faster download)</option>
              <option value="medium">Medium (Balanced)</option>
              <option value="high">High (Best quality)</option>
            </select>
          </div>

          <div className="settings-group">
            <label htmlFor="format">Image Format</label>
            <select
              data-testid="format-select"
              id="format"
              value={settings.format}
              onChange={(e) => handleChange("format", e.target.value)}
            >
              <option value="original">Original (As uploaded)</option>
              <option value="png">PNG (Lossless)</option>
              <option value="jpg">JPG (Compressed)</option>
            </select>
          </div>

          <button
            data-testid="save-settings-button"
            className="save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>

        <div className="settings-card" style={{ marginTop: '24px' }}>
          <h3>Account Information</h3>
          <div style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.8' }}>
            <p><strong style={{ color: '#334155' }}>Email:</strong> {user?.email}</p>
            <p><strong style={{ color: '#334155' }}>Username:</strong> {user?.username}</p>
            <p><strong style={{ color: '#334155' }}>Member since:</strong> {new Date(user?.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </main>
    </div>
  );
}